import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  getDoc,
  getDocs,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";

// ─── GET CONVERSATION ID ──────────────────────────────────────────────────────
function getConversationId(uid1, uid2) {
  return [uid1, uid2].sort().join("_");
}

// ─── SEND MESSAGE ─────────────────────────────────────────────────────────────
export async function sendMessage(fromUid, toUid, text) {
  const convId = getConversationId(fromUid, toUid);
  const convRef = doc(db, "conversations", convId);
  const convSnap = await getDoc(convRef);

  // Create or update conversation
  if (!convSnap.exists()) {
    await setDoc(convRef, {
      participants: [fromUid, toUid],
      createdAt: serverTimestamp(),
      lastMessage: text,
      lastMessageAt: serverTimestamp(),
      // Message requests — accepted by recipient
      acceptedBy: [fromUid], // sender auto-accepts
      requestFrom: fromUid,
    });
  } else {
    await updateDoc(convRef, {
      lastMessage: text,
      lastMessageAt: serverTimestamp(),
    });
  }

  // Add message
  await addDoc(collection(db, "conversations", convId, "messages"), {
    from: fromUid,
    text,
    createdAt: serverTimestamp(),
    read: false,
  });
}

// ─── ACCEPT MESSAGE REQUEST ───────────────────────────────────────────────────
export async function acceptMessageRequest(fromUid, toUid) {
  const convId = getConversationId(fromUid, toUid);
  await updateDoc(doc(db, "conversations", convId), {
    acceptedBy: [fromUid, toUid],
  });
}

// ─── DECLINE MESSAGE REQUEST ──────────────────────────────────────────────────
export async function declineMessageRequest(fromUid, toUid) {
  const convId = getConversationId(fromUid, toUid);
  await updateDoc(doc(db, "conversations", convId), {
    status: "declined",
  });
}

// ─── SUBSCRIBE TO MESSAGES ────────────────────────────────────────────────────
export function subscribeToMessages(uid1, uid2, callback) {
  const convId = getConversationId(uid1, uid2);
  const q = query(
    collection(db, "conversations", convId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── SUBSCRIBE TO ALL CONVERSATIONS ──────────────────────────────────────────
export function subscribeToConversations(uid, callback) {
  const q = query(
    collection(db, "conversations"),
    where("participants", "array-contains", uid)
  );
  return onSnapshot(q, (snap) => {
    const convs = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((c) => c.status !== "declined");
    callback(convs);
  });
}

// ─── GET MESSAGE REQUESTS ─────────────────────────────────────────────────────
export function subscribeToMessageRequests(uid, callback) {
  const q = query(
    collection(db, "conversations"),
    where("participants", "array-contains", uid)
  );
  return onSnapshot(q, (snap) => {
    const requests = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((c) => c.requestFrom !== uid && !c.acceptedBy?.includes(uid));
    callback(requests);
  });
}

// ─── MARK MESSAGES AS READ ────────────────────────────────────────────────────
export async function markMessagesRead(uid1, uid2) {
  const convId = getConversationId(uid1, uid2);
  const q = query(
    collection(db, "conversations", convId, "messages"),
    where("read", "==", false),
    where("from", "!=", uid1)
  );
  const snap = await getDocs(q);
  const updates = snap.docs.map((d) => updateDoc(d.ref, { read: true }));
  await Promise.all(updates);
}
